'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, Wifi, Activity, Bell, ChevronDown, ChevronUp, RefreshCw, Radio, Building2, Home, Mail, X, Loader2, ShieldCheck, Zap,  } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type Severity = 'p1' | 'p2' | 'p3' | 'p4';
type IncidentStatus = 'detected' | 'classified' | 'remediating' | 'verifying' | 'resolved' | 'closed';

interface Incident {
  id: string;
  title: string;
  severity: Severity;
  type: string;
  node: string;
  region: string;
  status: IncidentStatus;
  slaDeadline: string;
  slaBreaching: boolean;
  detectedAt: string;
  customer: string;
  estimatedRestoration?: string;
  affectedCount?: number;
}

// ─── Static incident data (mirrors IncidentTableSection) ─────────────────────

const ALL_INCIDENTS: Incident[] = [
  { id: 'INC-4821', title: 'Remote node receiver offline — 18 residential units affected', severity: 'p1', type: 'Remote Node Down', node: 'RN-NYC-APT-07', region: 'US-East', status: 'remediating', slaDeadline: '09:44:00', slaBreaching: true, detectedAt: '09:14:22', customer: 'Maple Court Apartments (MDU — 18 residents)', estimatedRestoration: 'Within 2 hours', affectedCount: 18 },
  { id: 'INC-4820', title: 'Base node RF link degraded — SNR below threshold', severity: 'p1', type: 'RF Link Degradation', node: 'BN-AMS-WEST-04', region: 'EU-West', status: 'detected', slaDeadline: '09:37:00', slaBreaching: true, detectedAt: '09:22:44', customer: 'EuroTelco GmbH (Business Complex — 14 tenants)', estimatedRestoration: 'Under investigation', affectedCount: 14 },
  { id: 'INC-4819', title: 'Remote node receiver unreachable — ICMP timeout all probes', severity: 'p1', type: 'Remote Node Down', node: 'RN-MIA-BIZ-02', region: 'US-South', status: 'classified', slaDeadline: '09:29:00', slaBreaching: false, detectedAt: '09:14:31', customer: 'Bayview Business Park (Business Complex — 9 tenants)', estimatedRestoration: 'Within 3 hours', affectedCount: 9 },
  { id: 'INC-4818', title: 'Tarana base node beam-forming instability — sector oscillation', severity: 'p2', type: 'Beam-Forming Instability', node: 'BN-SIN-NORTH-01', region: 'APAC', status: 'remediating', slaDeadline: '10:21:00', slaBreaching: false, detectedAt: '09:21:03', customer: 'PacificNet Ltd. (Business Complex — 12 tenants)', estimatedRestoration: 'Within 4 hours', affectedCount: 12 },
  { id: 'INC-4817', title: 'Upstream packet loss exceeds 3% — FWA backhaul link', severity: 'p2', type: 'Backhaul Packet Loss', node: 'BN-LAX-SOUTH-03', region: 'US-West', status: 'verifying', slaDeadline: '10:19:00', slaBreaching: false, detectedAt: '09:19:57', customer: 'Axiom Networks (Single Dwelling Business)', estimatedRestoration: 'Within 1 hour', affectedCount: 1 },
  { id: 'INC-4816', title: 'Remote node Rx power below minimum — signal path obstruction', severity: 'p2', type: 'Signal Degradation', node: 'RN-SIN-APT-05', region: 'APAC', status: 'classified', slaDeadline: '10:16:00', slaBreaching: false, detectedAt: '09:16:44', customer: 'Orchid Gardens Residences (MDU — 20 residents)', estimatedRestoration: 'Within 6 hours', affectedCount: 20 },
  { id: 'INC-4815', title: 'Base node CPU spike — scheduling overload on sector processor', severity: 'p2', type: 'CPU Overload', node: 'BN-NYC-EAST-01', region: 'US-East', status: 'remediating', slaDeadline: '10:18:00', slaBreaching: false, detectedAt: '09:18:22', customer: 'Axiom Networks (Single Dwelling Business)', estimatedRestoration: 'Within 2 hours', affectedCount: 1 },
];

const ACTIVE_STATUSES: IncidentStatus[] = ['detected', 'classified', 'remediating', 'verifying'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const severityConfig: Record<Severity, { label: string; color: string; bg: string; border: string }> = {
  p1: { label: 'P1 Critical', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)' },
  p2: { label: 'P2 High',     color: '#f97316', bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.3)' },
  p3: { label: 'P3 Medium',   color: '#eab308', bg: 'rgba(234,179,8,0.1)',  border: 'rgba(234,179,8,0.3)' },
  p4: { label: 'P4 Low',      color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.3)' },
};

const statusConfig: Record<IncidentStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  detected:    { label: 'Detected',    color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  icon: AlertTriangle },
  classified:  { label: 'Classified',  color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  icon: Activity },
  remediating: { label: 'Remediating', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)',  icon: Zap },
  verifying:   { label: 'Verifying',   color: '#06b6d4', bg: 'rgba(6,182,212,0.1)',   icon: ShieldCheck },
  resolved:    { label: 'Resolved',    color: '#10b981', bg: 'rgba(16,185,129,0.1)',  icon: CheckCircle2 },
  closed:      { label: 'Closed',      color: '#6b7280', bg: 'rgba(107,114,128,0.1)', icon: CheckCircle2 },
};

function getNodeType(node: string): 'BN' | 'RN' {
  return node.startsWith('BN') ? 'BN' : 'RN';
}

function getCustomerType(customer: string): 'mdu' | 'business' | 'single' {
  if (customer.toLowerCase().includes('mdu') || customer.toLowerCase().includes('resident')) return 'mdu';
  if (customer.toLowerCase().includes('business complex') || customer.toLowerCase().includes('tenants')) return 'business';
  return 'single';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function NodeTypeBadge({ node }: { node: string }) {
  const type = getNodeType(node);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wide"
      style={{
        background: type === 'BN' ? 'rgba(56,189,248,0.12)' : 'rgba(167,139,250,0.12)',
        color: type === 'BN' ? '#38bdf8' : '#a78bfa',
        border: `1px solid ${type === 'BN' ? 'rgba(56,189,248,0.25)' : 'rgba(167,139,250,0.25)'}`,
      }}
    >
      <Radio size={9} />
      {type === 'BN' ? 'Base Node' : 'Remote Node'}
    </span>
  );
}

function CustomerTypeBadge({ customer }: { customer: string }) {
  const type = getCustomerType(customer);
  const cfg = {
    mdu:      { icon: Building2, label: 'MDU Residential', color: '#34d399', bg: 'rgba(52,211,153,0.1)', border: 'rgba(52,211,153,0.25)' },
    business: { icon: Building2, label: 'Business Complex', color: '#fb923c', bg: 'rgba(251,146,60,0.1)', border: 'rgba(251,146,60,0.25)' },
    single:   { icon: Home,      label: 'Single Dwelling',  color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.25)' },
  }[type];
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      <Icon size={9} />
      {cfg.label}
    </span>
  );
}

// ─── Email Subscription Modal ─────────────────────────────────────────────────

interface SubscribeModalProps {
  incident: Incident;
  onClose: () => void;
}

function SubscribeModal({ incident, onClose }: SubscribeModalProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const { error: dbErr } = await supabase
        .from('incident_subscriptions')
        .upsert(
          { email: email.trim().toLowerCase(), node_id: incident.node, incident_id: incident.id, is_active: true },
          { onConflict: 'email,node_id' }
        );
      if (dbErr) throw new Error(dbErr.message);

      // Send confirmation email via edge function
      await supabase.functions.invoke('send-incident-update', {
        body: {
          email: email.trim().toLowerCase(),
          incidentId: incident.id,
          incidentTitle: incident.title,
          node: incident.node,
          status: incident.status,
          estimatedRestoration: incident.estimatedRestoration,
        },
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Subscription failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div
        className="relative w-full max-w-md rounded-xl p-6"
        style={{ background: '#1e293b', border: '1px solid #334155' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg transition-colors"
          style={{ color: '#64748b' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#334155')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <X size={16} />
        </button>

        {success ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(16,185,129,0.15)' }}>
              <CheckCircle2 size={24} style={{ color: '#10b981' }} />
            </div>
            <h3 className="text-base font-semibold mb-2" style={{ color: '#f1f5f9' }}>Subscribed Successfully</h3>
            <p className="text-sm mb-1" style={{ color: '#94a3b8' }}>
              You'll receive email updates for incident <span className="font-mono font-semibold" style={{ color: '#38bdf8' }}>{incident.id}</span>.
            </p>
            <p className="text-xs" style={{ color: '#64748b' }}>A confirmation has been sent to <strong style={{ color: '#94a3b8' }}>{email}</strong>.</p>
            <button
              onClick={onClose}
              className="mt-5 px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{ background: '#334155', color: '#f1f5f9' }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56,189,248,0.12)' }}>
                <Bell size={18} style={{ color: '#38bdf8' }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: '#f1f5f9' }}>Subscribe to Updates</h3>
                <p className="text-xs" style={{ color: '#64748b' }}>Get live email alerts for this incident</p>
              </div>
            </div>

            <div className="rounded-lg p-3 mb-5" style={{ background: '#0f172a', border: '1px solid #1e3a5f' }}>
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: '#475569' }}>Incident</p>
              <p className="text-xs font-mono font-bold mb-1" style={{ color: '#38bdf8' }}>{incident.id}</p>
              <p className="text-xs leading-relaxed" style={{ color: '#94a3b8' }}>{incident.title}</p>
              <div className="flex items-center gap-2 mt-2">
                <NodeTypeBadge node={incident.node} />
                <span className="text-xs font-mono" style={{ color: '#64748b' }}>{incident.node}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#94a3b8' }}>
                  Your Email Address
                </label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#475569' }} />
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="you@example.com"
                    className="w-full pl-9 pr-4 py-2.5 rounded-lg text-sm outline-none transition-colors"
                    style={{
                      background: '#0f172a',
                      border: `1px solid ${error ? '#ef4444' : '#334155'}`,
                      color: '#f1f5f9',
                    }}
                    onFocus={e => (e.currentTarget.style.borderColor = '#38bdf8')}
                    onBlur={e => (e.currentTarget.style.borderColor = error ? '#ef4444' : '#334155')}
                  />
                </div>
                {error && <p className="text-xs mt-1.5" style={{ color: '#ef4444' }}>{error}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-opacity"
                style={{ background: '#0ea5e9', color: '#fff', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? (
                  <><Loader2 size={14} className="animate-spin" /> Subscribing…</>
                ) : (
                  <><Bell size={14} /> Subscribe to Updates</>
                )}
              </button>
            </form>

            <p className="text-[11px] text-center mt-3" style={{ color: '#475569' }}>
              You can unsubscribe at any time. We'll only send incident-related updates.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Incident Card ────────────────────────────────────────────────────────────

interface IncidentCardProps {
  incident: Incident;
  onSubscribe: (incident: Incident) => void;
}

function IncidentCard({ incident, onSubscribe }: IncidentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const sev = severityConfig[incident.severity];
  const st = statusConfig[incident.status];
  const StatusIcon = st.icon;

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-200"
      style={{ background: '#1e293b', border: `1px solid ${sev.border}` }}
    >
      {/* Severity stripe */}
      <div className="h-0.5" style={{ background: sev.color }} />

      <div className="p-5">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono font-bold" style={{ color: '#64748b' }}>{incident.id}</span>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded"
              style={{ background: sev.bg, color: sev.color, border: `1px solid ${sev.border}` }}
            >
              {sev.label}
            </span>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded"
              style={{ background: st.bg, color: st.color }}
            >
              <StatusIcon size={9} />
              {st.label}
            </span>
            {incident.slaBreaching && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded animate-pulse" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                SLA BREACH
              </span>
            )}
          </div>
          <button
            onClick={() => onSubscribe(incident)}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{ background: 'rgba(56,189,248,0.1)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.2)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(56,189,248,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(56,189,248,0.1)'; }}
          >
            <Bell size={11} />
            Get Updates
          </button>
        </div>

        {/* Title */}
        <p className="text-sm font-medium leading-snug mb-3" style={{ color: '#e2e8f0' }}>
          {incident.title}
        </p>

        {/* Node + customer row */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <NodeTypeBadge node={incident.node} />
          <span className="text-xs font-mono font-semibold" style={{ color: '#38bdf8' }}>{incident.node}</span>
          <span className="text-xs" style={{ color: '#475569' }}>·</span>
          <CustomerTypeBadge customer={incident.customer} />
        </div>

        {/* Key info strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: '#475569' }}>Region</p>
            <p className="text-xs font-medium" style={{ color: '#94a3b8' }}>{incident.region}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: '#475569' }}>Detected</p>
            <p className="text-xs font-mono" style={{ color: '#94a3b8' }}>{incident.detectedAt}</p>
          </div>
          {incident.estimatedRestoration && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: '#475569' }}>Est. Restoration</p>
              <p className="text-xs font-semibold" style={{ color: '#34d399' }}>{incident.estimatedRestoration}</p>
            </div>
          )}
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs transition-colors"
          style={{ color: '#64748b' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#94a3b8')}
          onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}
        >
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {expanded ? 'Show less' : 'Show details'}
        </button>

        {expanded && (
          <div className="mt-3 pt-3 space-y-2" style={{ borderTop: '1px solid #1e3a5f' }}>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: '#475569' }}>Incident Type</p>
              <p className="text-xs" style={{ color: '#94a3b8' }}>{incident.type}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: '#475569' }}>Customer</p>
              <p className="text-xs" style={{ color: '#94a3b8' }}>{incident.customer}</p>
            </div>
            {incident.affectedCount !== undefined && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: '#475569' }}>Affected Units</p>
                <p className="text-xs font-semibold" style={{ color: '#f97316' }}>
                  {incident.affectedCount} {incident.affectedCount === 1 ? 'unit' : 'units'}
                </p>
              </div>
            )}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: '#475569' }}>SLA Deadline</p>
              <p className="text-xs font-mono" style={{ color: incident.slaBreaching ? '#ef4444' : '#94a3b8' }}>
                {incident.slaDeadline}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Node Search ──────────────────────────────────────────────────────────────

interface NodeSearchProps {
  value: string;
  onChange: (v: string) => void;
}

function NodeSearch({ value, onChange }: NodeSearchProps) {
  const allNodes = [...new Set(ALL_INCIDENTS.map(i => i.node))].sort();

  return (
    <div className="relative">
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: '#1e293b', border: '1px solid #334155' }}>
        <Wifi size={15} style={{ color: '#38bdf8', flexShrink: 0 }} />
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Enter your node ID (e.g. RN-NYC-APT-07)"
          className="flex-1 bg-transparent text-sm outline-none"
          style={{ color: '#f1f5f9' }}
          list="node-list"
        />
        {value && (
          <button onClick={() => onChange('')} style={{ color: '#64748b' }}>
            <X size={13} />
          </button>
        )}
      </div>
      <datalist id="node-list">
        {allNodes.map(n => <option key={n} value={n} />)}
      </datalist>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IncidentStatusPage() {
  const [nodeSearch, setNodeSearch] = useState('');
  const [subscribeTarget, setSubscribeTarget] = useState<Incident | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);

  const activeIncidents = ALL_INCIDENTS.filter(i => ACTIVE_STATUSES.includes(i.status));

  const filteredIncidents = nodeSearch.trim()
    ? activeIncidents.filter(i =>
        i.node.toLowerCase().includes(nodeSearch.trim().toLowerCase()) ||
        i.customer.toLowerCase().includes(nodeSearch.trim().toLowerCase())
      )
    : activeIncidents;

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    setTimeout(() => {
      setLastRefresh(new Date());
      setRefreshing(false);
    }, 800);
  }, []);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const timer = setInterval(handleRefresh, 60000);
    return () => clearInterval(timer);
  }, [handleRefresh]);

  const p1Count = filteredIncidents.filter(i => i.severity === 'p1').length;
  const p2Count = filteredIncidents.filter(i => i.severity === 'p2').length;
  const remediatingCount = filteredIncidents.filter(i => i.status === 'remediating').length;

  return (
    <div className="min-h-screen" style={{ background: '#0f172a' }}>
      {/* Header */}
      <div style={{ background: '#1e293b', borderBottom: '1px solid #334155' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#ef4444' }} />
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>
                  Live Network Status
                </span>
              </div>
              <h1 className="text-xl font-bold" style={{ color: '#f1f5f9' }}>
                Active Incident Status
              </h1>
              <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
                Tarana Fixed Wireless · Real-time updates · Subscribe for email alerts
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all flex-shrink-0"
              style={{ background: '#334155', color: '#94a3b8' }}
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Active Incidents', value: activeIncidents.length, color: '#f1f5f9', icon: AlertTriangle, iconColor: '#f59e0b' },
            { label: 'P1 Critical',      value: p1Count,               color: '#ef4444', icon: AlertTriangle, iconColor: '#ef4444' },
            { label: 'P2 High',          value: p2Count,               color: '#f97316', icon: Activity,      iconColor: '#f97316' },
            { label: 'Remediating',      value: remediatingCount,      color: '#8b5cf6', icon: Zap,           iconColor: '#8b5cf6' },
          ].map(kpi => {
            const KpiIcon = kpi.icon;
            return (
              <div key={kpi.label} className="rounded-xl p-4" style={{ background: '#1e293b', border: '1px solid #334155' }}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#475569' }}>{kpi.label}</p>
                  <KpiIcon size={13} style={{ color: kpi.iconColor }} />
                </div>
                <p className="text-2xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
              </div>
            );
          })}
        </div>

        {/* Node search */}
        <div>
          <p className="text-xs font-semibold mb-2" style={{ color: '#64748b' }}>
            Filter by your node or customer name
          </p>
          <NodeSearch value={nodeSearch} onChange={setNodeSearch} />
          {nodeSearch && (
            <p className="text-xs mt-2" style={{ color: '#64748b' }}>
              {filteredIncidents.length === 0
                ? 'No active incidents found for this node — your service appears unaffected.'
                : `${filteredIncidents.length} active incident${filteredIncidents.length !== 1 ? 's' : ''} affecting your node`}
            </p>
          )}
        </div>

        {/* No active incidents for node */}
        {nodeSearch && filteredIncidents.length === 0 && (
          <div className="rounded-xl p-8 text-center" style={{ background: '#1e293b', border: '1px solid #334155' }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(16,185,129,0.12)' }}>
              <CheckCircle2 size={24} style={{ color: '#10b981' }} />
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: '#f1f5f9' }}>No Active Incidents</p>
            <p className="text-xs" style={{ color: '#64748b' }}>
              Node <span className="font-mono font-bold" style={{ color: '#38bdf8' }}>{nodeSearch}</span> has no active incidents at this time.
            </p>
          </div>
        )}

        {/* Incident list */}
        {filteredIncidents.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#475569' }}>
                {nodeSearch ? 'Incidents Affecting Your Node' : 'All Active Incidents'}
              </p>
              <p className="text-[10px]" style={{ color: '#475569' }}>
                Last updated: {lastRefresh.toLocaleTimeString()}
              </p>
            </div>
            {filteredIncidents.map(incident => (
              <IncidentCard
                key={incident.id}
                incident={incident}
                onSubscribe={setSubscribeTarget}
              />
            ))}
          </div>
        )}

        {/* All incidents (when no filter) — show resolved/closed separately */}
        {!nodeSearch && (
          <div className="rounded-xl p-5" style={{ background: '#1e293b', border: '1px solid #334155' }}>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={14} style={{ color: '#10b981' }} />
              <p className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
                Service Status — All Regions
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {['US-East', 'US-West', 'US-South', 'US-Central', 'EU-West', 'EU-Central', 'APAC'].map(region => {
                const regionActive = activeIncidents.filter(i => i.region === region);
                return (
                  <div key={region} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: '#0f172a' }}>
                    <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>{region}</span>
                    {regionActive.length === 0 ? (
                      <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: '#10b981' }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#10b981' }} />
                        Operational
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: '#ef4444' }}>
                        <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#ef4444' }} />
                        {regionActive.length} incident{regionActive.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Info footer */}
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.15)' }}>
          <Bell size={14} style={{ color: '#38bdf8', flexShrink: 0, marginTop: 1 }} />
          <p className="text-xs leading-relaxed" style={{ color: '#64748b' }}>
            Click <strong style={{ color: '#94a3b8' }}>Get Updates</strong> on any incident to subscribe for real-time email notifications. You'll receive an email whenever the incident status changes or when service is restored.
          </p>
        </div>

      </div>

      {/* Subscribe modal */}
      {subscribeTarget && (
        <SubscribeModal
          incident={subscribeTarget}
          onClose={() => setSubscribeTarget(null)}
        />
      )}
    </div>
  );
}
